import { useEffect, useMemo, useSyncExternalStore } from 'react';

import { useCollectionScope } from '../contexts/CollectionScopeContext';
import { getCollection, listCollections } from '../services/collections';
import { collectionIdentityOf, qualifierIdOf } from '../utils/recordQualifier';
import { normalizeId } from '../utils/ids';
import { getSessionKey, getSessionUserId, onSessionChange } from '../utils/session';

/**
 * WHAT A COLLECTION IS, BY ID — `{ _id, name, icon, color, parentName? }` —
 * for the qualified badge (utils/recordQualifier.js), which has to name the
 * collection that owns a record shown outside it.
 *
 * ONE resolver, one session cache, SEEDED ONLY FROM THE SERVER'S OWN ANSWERS
 * (`seedCollectionIdentities`): the account's collections list (the rail, the
 * palette — `complete`), the collection a layout opened (`complete`) and its
 * family summaries (`hierarchy`), and the link summaries and lenders
 * `useLinkedSources` read. An identity a caller hands a badge directly (a
 * row's `linkedFrom`, a card's `borrowedFrom`, a pin's or a draft's
 * `collection`) is SHOWN — completed by whatever the cache holds for the same
 * id — but never remembered: a caller may hold only part of it, and what one
 * surface happened to hold must not become every surface's answer.
 *
 * NEVER A DOWNGRADE. A partial answer (no icon, no colour, no parent) never
 * erases what a fuller one said; only a `complete` record — a list row, a
 * collection document, where a null means "none" — replaces an entry whole.
 * A placeholder ("another collection", an empty name) is never an identity
 * (utils/recordQualifier collectionIdentityOf refuses a nameless one).
 *
 * An id nobody seeded is resolved from the server, batched and deduplicated:
 * the first unknown id of a session reads the account's collections list
 * once (it answers every collection the person belongs to at once), and any
 * id still unknown after it is read on its own — `GET /api/collections/{id}`,
 * a few at a time.
 *
 * WHAT A FAILURE MEANS. A 404, a 403, or an answer that is not the collection
 * asked for is "this reader cannot name it": remembered as null, and the badge
 * renders UNQUALIFIED rather than with a broken segment — until the entry
 * expires (`negativeTtl`), after which the next badge that asks reads it again
 * (a collection shared with the reader mid-session then resolves; a later
 * list read resolves it at once). Anything else — a 5xx, a 429, a dropped
 * connection — is not an answer: the list read is forgotten (the next
 * unknown id reads it again) and the id is retried after a short, growing
 * wait, a bounded number of times, then rests as unresolvable like a 404.
 * Nothing here learns anything the viewer could not already read.
 *
 * Signed out, nothing is asked (a signed-out read is a 401, and the public
 * pages turn qualification off altogether: contexts/CollectionScopeContext).
 *
 * PER SESSION (utils/session.js): entries are stamped with the session that
 * read them, served to that session only, and dropped when the account
 * changes; an answer that lands for another account is discarded — a seeder
 * passes the `session` its request was made under — and a per-id read the
 * last account queued but had not sent is never sent.
 */

const PER_ID_CONCURRENCY = 4;

const DEFAULT_TIMINGS = Object.freeze({
	// How long "cannot be named" (a 404, a 403, a malformed answer) stands.
	negativeTtl: 5 * 60 * 1000,
	// The waits before each retry of a transient failure; then it rests.
	retryDelays: [2000, 10000, 30000],
	now: () => Date.now(),
});
let timings = DEFAULT_TIMINGS;

const store = {
	owner: null,
	entries: new Map(), // id -> identity | null (unresolvable, for now)
	// Ids whose entry is a COMPLETE record (a list row, a document): a null
	// icon or colour there is the collection's own, not a gap to fill.
	complete: new Set(),
	expires: new Map(), // id -> when a null entry may be asked again
	failures: new Map(), // id -> transient failures in a row
	// The one list read of this session, shared by every flush that lands
	// while it is in flight (null until the first unknown id asks, and again
	// after a read that failed).
	listRead: null,
};
const inFlight = new Set();
// id -> the timer that ends its retry wait. While one is armed the id is
// WAITING: only that timer may ask for it again (KPR-1645).
const waiting = new Map();
let queue = new Set();
let queueOwner = null;
let flushScheduled = false;
let version = 0;
// Bumped when the signed-in account changes, so a badge waiting on an id asks
// again for the next account (whose answer may differ).
let sessionEpoch = 0;
const listeners = new Set();

const emit = () => {
	version += 1;
	listeners.forEach(listener => listener());
};

function forget(owner) {
	store.owner = owner;
	store.entries = new Map();
	store.complete = new Set();
	store.expires = new Map();
	store.failures = new Map();
	store.listRead = null;
	inFlight.clear();
	waiting.forEach(timer => clearTimeout(timer));
	waiting.clear();
	queue = new Set();
	queueOwner = null;
}

// The entries as THIS session sees them: another session's are dropped the
// moment they are asked for.
function held() {
	const key = getSessionKey();
	if (store.owner !== key) forget(key);
	return store.entries;
}

onSessionChange(() => {
	sessionEpoch += 1;
	forget(getSessionKey());
	emit();
});

const sameIdentity = (a, b) => Boolean(a && b)
	&& a.name === b.name
	&& (a.icon || null) === (b.icon || null)
	&& (a.color || null) === (b.color || null)
	&& (a.parentName || null) === (b.parentName || null);

// `base` with the gaps `extra` can fill — never the other way round: a
// partial answer never erases what a fuller one said.
const filled = (base, extra) => {
	if (!extra) return base;
	const icon = base.icon || extra.icon || null;
	const color = base.color || extra.color || null;
	const parentName = base.parentName || extra.parentName || null;
	return { ...base, icon, color, ...(parentName ? { parentName } : null) };
};

/**
 * Teach the cache what the SERVER said: any list of collection-shaped
 * objects (see utils/recordQualifier collectionIdentityOf). Entries without
 * both an id and a real name are ignored. A partial answer updates the name
 * (a rename reaches every badge) and any icon, colour or parent it carries,
 * but never erases one already known; `complete: true` (a collections list,
 * a collection document) replaces the entry whole. `session`: the session
 * the answer was asked for under — an answer for another account is dropped.
 * Never called during render.
 */
export function seedCollectionIdentities(list, { complete = false, session = null } = {}) {
	if (session && session !== getSessionKey()) return;
	const rows = Array.isArray(list) ? list : [list];
	const entries = held();
	let changed = false;
	rows.forEach(raw => {
		const identity = collectionIdentityOf(raw);
		if (!identity) return;
		const id = identity._id;
		const current = entries.get(id);
		const next = complete || !current ? identity : filled(identity, current);
		store.expires.delete(id);
		store.failures.delete(id);
		// The server has answered: a retry still waiting is not needed.
		clearTimeout(waiting.get(id));
		waiting.delete(id);
		if (complete) store.complete.add(id);
		if (sameIdentity(current, next)) return;
		entries.set(id, next);
		changed = true;
	});
	if (changed) emit();
}

/** What the cache holds for `id` right now: an identity, null (unresolvable), or undefined (not asked yet). */
export function peekCollectionIdentity(id) {
	const key = normalizeId(id);
	return key ? held().get(key) : undefined;
}

async function settle(tasks, limit) {
	const queued = [...tasks];
	const workers = Array.from({ length: Math.min(limit, queued.length) }, async () => {
		while (queued.length) {
			const task = queued.shift();
			// eslint-disable-next-line no-await-in-loop
			await task();
		}
	});
	await Promise.all(workers);
}

// A 404 or a 403 is the server saying this reader cannot open it; anything
// else that fails (a 5xx, a 429, a network error, a 401 on the way out) says
// nothing about the collection.
const isRefusal = error => [403, 404].includes(error?.response?.status);

function rememberUnresolvable(id, ttl) {
	store.entries.set(id, null);
	store.expires.set(id, timings.now() + ttl);
}

// A transient failure: unqualified for a short, growing while (so nothing
// re-asks in a loop meanwhile), then asked again — a bounded number of times.
// The wait is owned by its timer, not by the clock: a request that lands
// after the expiry but before a (late) timer must not ask, or that ask would
// fall outside the count and the timer, still armed, would ask once more.
function retryLater(id, owner) {
	const attempt = (store.failures.get(id) || 0) + 1;
	store.failures.set(id, attempt);
	const delay = timings.retryDelays[attempt - 1];
	if (delay === undefined) {
		rememberUnresolvable(id, timings.negativeTtl);
		return;
	}
	rememberUnresolvable(id, delay);
	clearTimeout(waiting.get(id));
	const timer = setTimeout(() => {
		if (waiting.get(id) === timer) waiting.delete(id);
		if (store.owner !== owner || getSessionKey() !== owner) return;
		// A timer can fire a millisecond before the clock reaches the expiry it
		// was armed with; the wait is over either way, so expire it now or the
		// retry would be dropped as "still unresolvable".
		if (held().get(id) === null) store.expires.set(id, timings.now());
		requestCollectionIdentities([id]);
	}, delay);
	waiting.set(id, timer);
}

async function flush() {
	flushScheduled = false;
	const owner = queueOwner;
	const wanted = [...queue];
	queue = new Set();
	queueOwner = null;
	// Asked for by an account that is no longer signed in: dropped, unsent.
	if (!wanted.length || owner !== getSessionKey()) return;
	wanted.forEach(id => inFlight.add(id));
	const stillOurs = () => getSessionKey() === owner && store.owner === owner;
	try {
		if (!store.listRead) {
			const read = Promise.resolve().then(listCollections).then(rows => {
				seedCollectionIdentities(rows, { complete: true, session: owner });
			}, () => {
				// Not an answer: the next unknown id reads the list again.
				if (store.listRead === read) store.listRead = null;
			});
			store.listRead = read;
		}
		await store.listRead;
		if (!stillOurs()) return;
		const left = wanted.filter(id => held().get(id) === undefined);
		await settle(left.map(id => async () => {
			// An account change drops every read not yet sent.
			if (!stillOurs()) return;
			let doc;
			try {
				doc = await getCollection(id);
			} catch (error) {
				if (!stillOurs()) return;
				// A refusal is an answer; anything else is left for the retry below.
				if (isRefusal(error)) rememberUnresolvable(id, timings.negativeTtl);
				return;
			}
			if (!stillOurs()) return;
			// The answer must BE the collection asked for; anything else (an
			// error page, a list) is no answer, and the badge stays unqualified.
			const identity = doc && normalizeId(doc._id) === id ? collectionIdentityOf(doc) : null;
			if (identity) seedCollectionIdentities([doc], { complete: true, session: owner });
			else if (held().get(id) === undefined) rememberUnresolvable(id, timings.negativeTtl);
		}), PER_ID_CONCURRENCY);
	} finally {
		wanted.forEach(id => inFlight.delete(id));
		if (stillOurs()) {
			// A transient failure — or no answer at all — is asked again later.
			wanted.forEach(id => { if (held().get(id) === undefined) retryLater(id, owner); });
			emit();
		}
	}
}

/**
 * Ask the server about ids nothing seeded (deduplicated, batched into one
 * flush per tick). An id remembered as unresolvable is asked again only once
 * that has expired. Signed out: nothing.
 */
export function requestCollectionIdentities(ids) {
	if (!getSessionUserId()) return;
	const entries = held();
	const now = timings.now();
	(Array.isArray(ids) ? ids : [ids]).forEach(raw => {
		const id = normalizeId(raw);
		if (!id || inFlight.has(id) || waiting.has(id)) return;
		const current = entries.get(id);
		if (current === null && (store.expires.get(id) ?? Infinity) <= now) {
			entries.delete(id);
			store.expires.delete(id);
		} else if (current !== undefined) {
			return;
		}
		queue.add(id);
	});
	if (!queue.size) return;
	queueOwner = store.owner;
	if (flushScheduled) return;
	flushScheduled = true;
	Promise.resolve().then(flush).catch(() => {});
}

const subscribe = listener => {
	listeners.add(listener);
	return () => listeners.delete(listener);
};
const noSubscribe = () => () => {};

/**
 * The identity to SHOW for `id`, given what a caller holds (`given`, may be
 * null) and what the cache holds (`stored`): the cache's — the server's own
 * answer — completed from the caller's where the cache has a gap (unless the
 * cache's entry is a complete record); the caller's alone while the cache has
 * nothing. Whatever a badge prints and whatever it speaks come from this one
 * answer.
 */
export function resolveCollectionIdentity(stored, given, id) {
	if (stored) return store.complete.has(id) ? stored : filled(stored, given);
	return given || null;
}

/**
 * One collection's identity: `raw` is an identity object (shown, completed by
 * the cache — never remembered) or an id (read from the cache, and asked of
 * the server when unknown and `fetch` is on). Null until known, and null
 * while it cannot be resolved.
 */
export default function useCollectionIdentity(raw, { fetch = true } = {}) {
	const direct = collectionIdentityOf(raw);
	const directKey = direct ? `${direct._id}|${direct.name}|${direct.icon || ''}|${direct.color || ''}|${direct.parentName || ''}` : '';
	const given = useMemo(() => direct, [directKey]); // eslint-disable-line react-hooks/exhaustive-deps
	const id = given ? given._id : qualifierIdOf(raw);
	const session = useSyncExternalStore(id ? subscribe : noSubscribe, () => sessionEpoch, () => 0);
	const stored = useSyncExternalStore(
		id ? subscribe : noSubscribe,
		() => (id ? held().get(id) : undefined),
		() => undefined
	);
	const isComplete = Boolean(id) && store.complete.has(id);

	// A caller that already holds a name needs no request; an id alone is
	// asked for (and asked again once "unresolvable" has expired).
	useEffect(() => {
		if (id && !given && fetch && (stored === undefined || stored === null)) requestCollectionIdentities([id]);
	}, [id, given, fetch, stored, session]);

	return useMemo(
		() => resolveCollectionIdentity(stored, given, id),
		[stored, given, id, isComplete] // eslint-disable-line react-hooks/exhaustive-deps
	);
}

/**
 * Several collections' identities at once (a lookup value pointing into more
 * than one): `Map(id → identity)` holding only the ids resolved so far.
 * Unknown ids are asked of the server once.
 */
export function useCollectionIdentities(ids, { fetch = true } = {}) {
	const list = useMemo(
		() => [...new Set((Array.isArray(ids) ? ids : []).map(normalizeId).filter(Boolean))].sort(),
		[ids]
	);
	const listKey = list.join(',');
	const session = useSyncExternalStore(list.length ? subscribe : noSubscribe, () => sessionEpoch, () => 0);
	const stamp = useSyncExternalStore(list.length ? subscribe : noSubscribe, () => version, () => 0);
	const resolved = useMemo(() => {
		const map = new Map();
		const entries = held();
		list.forEach(id => {
			const identity = entries.get(id);
			if (identity) map.set(id, identity);
		});
		return map;
	}, [listKey, stamp, session]); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if (!fetch || !list.length) return;
		// Unknown ids, and unresolvable ones (the request re-asks only those expired).
		const unknown = list.filter(id => !peekCollectionIdentity(id));
		if (unknown.length) requestCollectionIdentities(unknown);
	}, [listKey, fetch, stamp, session]); // eslint-disable-line react-hooks/exhaustive-deps

	return resolved;
}

/**
 * THE QUALIFIER FOR A BADGE: the identity of `collection` (an id or an
 * identity object — the collection that owns the record) when the surface the
 * badge is drawn on is NOT that collection, else null. Null too on a surface
 * that turned qualification off (public pages, paper), and while — or when —
 * the identity cannot be resolved: an unqualified badge, never a broken one.
 */
export function useQualifier(collection) {
	const scope = useCollectionScope();
	const ownerId = qualifierIdOf(collection);
	const active = scope.qualify !== false && Boolean(ownerId) && ownerId !== scope.collectionId;
	const identity = useCollectionIdentity(active ? collection : null);
	return active ? identity : null;
}

/** Test helper: forget everything. */
export function _resetCollectionIdentities() {
	forget(null);
	flushScheduled = false;
	emit();
}

/** Test helper: override the clock, the negative TTL or the retry waits (null restores the defaults). */
export function _setCollectionIdentityTimings(overrides) {
	timings = overrides ? { ...DEFAULT_TIMINGS, ...overrides } : DEFAULT_TIMINGS;
}
